import { supabase } from '@/lib/supabase'
import type {
  TransientStayTableRow,
  TransientStayFormData,
  TransientStayFilters,
  BookingType,
  TransientStatus,
  BookingSource,
  SizeCategory,
  RateType,
} from '@/types/transient-stays'

/**
 * Fetch all transient stays with optional filtering
 * Includes joins to clients, aircraft, units, locations
 */
export async function fetchTransientStays(
  organizationId: string,
  filters?: TransientStayFilters
): Promise<TransientStayTableRow[]> {
  let query = supabase
    .from('transient_stays')
    .select(`
      id,
      organization_id,
      location_id,
      booking_number,
      client_id,
      aircraft_id,
      aircraft_type_id,
      unit_id,
      check_in,
      expected_check_out,
      actual_check_out,
      nights_count,
      sqft_required,
      wingspan,
      length,
      size_category,
      booking_type,
      source,
      status,
      rate_type,
      rate_amount,
      subtotal,
      discount_amount,
      discount_reason,
      tax_amount,
      total_amount,
      invoice_id,
      invoiced_at,
      allocation_id,
      notes,
      special_requests,
      cancellation_reason,
      created_at,
      updated_at,
      client:client_id (
        client_type,
        first_name,
        last_name,
        company_name
      ),
      aircraft:aircraft_id (
        tail_number,
        aircraft_types:aircraft_type_id (
          manufacturer,
          model
        )
      ),
      unit:unit_id (
        unit_code,
        type,
        area_id,
        area:area_id (
          name
        )
      ),
      location:location_id (
        name
      )
    `)
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .order('check_in', { ascending: false })

  // Apply filters
  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status)
  }

  if (filters?.bookingType && filters.bookingType.length > 0) {
    query = query.in('booking_type', filters.bookingType)
  }

  if (filters?.locationId) {
    query = query.eq('location_id', filters.locationId)
  }

  if (filters?.dateFrom) {
    query = query.gte('check_in', filters.dateFrom)
  }

  if (filters?.dateTo) {
    query = query.lte('check_in', filters.dateTo)
  }

  if (filters?.search) {
    const search = filters.search.toLowerCase()
    query = query.or(`booking_number.ilike.%${search}%`)
  }

  const { data, error } = await query

  if (error) throw error

  // Transform joined data to flat structure
  return (data ?? []).map((row): TransientStayTableRow => {
    const client = (row.client as unknown as {
      client_type: string
      first_name: string | null
      last_name: string | null
      company_name: string | null
    } | null)
    // Compute client name based on type
    const clientName = client?.client_type === 'individual'
      ? [client.first_name, client.last_name].filter(Boolean).join(' ') || null
      : client?.company_name ?? null
    const aircraft = (row.aircraft as unknown as {
      tail_number: string
      aircraft_types: { manufacturer: string; model: string } | null
    } | null)
    const unit = (row.unit as unknown as {
      unit_code: string
      type: string | null
      area_id: string | null
      area: { name: string } | null
    } | null)
    const location = (row.location as unknown as { name: string } | null)

    return {
      id: row.id,
      organization_id: row.organization_id,
      location_id: row.location_id,
      booking_number: row.booking_number,
      client_id: row.client_id,
      client_name: clientName,
      aircraft_id: row.aircraft_id,
      tail_number: aircraft?.tail_number ?? null,
      aircraft_type_id: row.aircraft_type_id,
      aircraft_type_name: aircraft?.aircraft_types
        ? `${aircraft.aircraft_types.manufacturer} ${aircraft.aircraft_types.model}`
        : null,
      unit_id: row.unit_id,
      unit_code: unit?.unit_code ?? null,
      unit_type: unit?.type ?? null,
      building_id: unit?.area_id ?? null,
      building_name: unit?.area?.name ?? null,
      location_name: location?.name ?? null,
      check_in: row.check_in,
      expected_check_out: row.expected_check_out,
      actual_check_out: row.actual_check_out,
      nights_count: row.nights_count,
      sqft_required: row.sqft_required,
      wingspan: row.wingspan,
      length: row.length,
      size_category: row.size_category as SizeCategory | null,
      booking_type: row.booking_type as BookingType,
      source: row.source as BookingSource | null,
      status: row.status as TransientStatus,
      rate_type: row.rate_type as RateType | null,
      rate_amount: row.rate_amount,
      subtotal: row.subtotal,
      discount_amount: row.discount_amount,
      discount_reason: row.discount_reason,
      tax_amount: row.tax_amount,
      total_amount: row.total_amount,
      invoice_id: row.invoice_id,
      invoiced_at: row.invoiced_at,
      allocation_id: row.allocation_id,
      notes: row.notes,
      special_requests: row.special_requests,
      cancellation_reason: row.cancellation_reason ?? null,
      created_at: row.created_at,
      updated_at: row.updated_at,
    }
  })
}

/**
 * Fetch a single transient stay by ID with full details
 */
export async function fetchTransientStayById(id: string): Promise<TransientStayTableRow> {
  const { data, error } = await supabase
    .from('transient_stays')
    .select(`
      id,
      organization_id,
      location_id,
      booking_number,
      client_id,
      aircraft_id,
      aircraft_type_id,
      unit_id,
      check_in,
      expected_check_out,
      actual_check_out,
      nights_count,
      sqft_required,
      wingspan,
      length,
      size_category,
      booking_type,
      source,
      status,
      rate_type,
      rate_amount,
      subtotal,
      discount_amount,
      discount_reason,
      tax_amount,
      total_amount,
      invoice_id,
      invoiced_at,
      allocation_id,
      notes,
      special_requests,
      cancellation_reason,
      created_at,
      updated_at,
      client:client_id (
        client_type,
        first_name,
        last_name,
        company_name
      ),
      aircraft:aircraft_id (
        tail_number,
        aircraft_types:aircraft_type_id (
          manufacturer,
          model
        )
      ),
      unit:unit_id (
        unit_code,
        type,
        area_id,
        area:area_id (
          name
        )
      ),
      location:location_id (
        name
      )
    `)
    .eq('id', id)
    .single()

  if (error) throw error

  const client = (data.client as unknown as {
    client_type: string
    first_name: string | null
    last_name: string | null
    company_name: string | null
  } | null)
  // Compute client name based on type
  const clientName = client?.client_type === 'individual'
    ? [client.first_name, client.last_name].filter(Boolean).join(' ') || null
    : client?.company_name ?? null
  const aircraft = (data.aircraft as unknown as {
    tail_number: string
    aircraft_types: { name: string } | null
  } | null)
  const unit = (data.unit as unknown as {
    unit_code: string
    type: string | null
    area_id: string | null
    area: { name: string } | null
  } | null)
  const location = (data.location as unknown as { name: string } | null)

  return {
    id: data.id,
    organization_id: data.organization_id,
    location_id: data.location_id,
    booking_number: data.booking_number,
    client_id: data.client_id,
    client_name: clientName,
    aircraft_id: data.aircraft_id,
    tail_number: aircraft?.tail_number ?? null,
    aircraft_type_id: data.aircraft_type_id,
    aircraft_type_name: aircraft?.aircraft_types
        ? `${aircraft.aircraft_types.manufacturer} ${aircraft.aircraft_types.model}`
        : null,
    unit_id: data.unit_id,
    unit_code: unit?.unit_code ?? null,
    unit_type: unit?.type ?? null,
    building_id: unit?.area_id ?? null,
    building_name: unit?.area?.name ?? null,
    location_name: location?.name ?? null,
    check_in: data.check_in,
    expected_check_out: data.expected_check_out,
    actual_check_out: data.actual_check_out,
    nights_count: data.nights_count,
    sqft_required: data.sqft_required,
    wingspan: data.wingspan,
    length: data.length,
    size_category: data.size_category as SizeCategory | null,
    booking_type: data.booking_type as BookingType,
    source: data.source as BookingSource | null,
    status: data.status as TransientStatus,
    rate_type: data.rate_type as RateType | null,
    rate_amount: data.rate_amount,
    subtotal: data.subtotal,
    discount_amount: data.discount_amount,
    discount_reason: data.discount_reason,
    tax_amount: data.tax_amount,
    total_amount: data.total_amount,
    invoice_id: data.invoice_id,
    invoiced_at: data.invoiced_at,
    allocation_id: data.allocation_id,
    notes: data.notes,
    special_requests: data.special_requests,
    cancellation_reason: data.cancellation_reason ?? null,
    created_at: data.created_at,
    updated_at: data.updated_at,
  }
}

/**
 * Create a new transient stay
 * booking_number is auto-generated by database trigger
 */
export async function createTransientStay(
  organizationId: string,
  data: TransientStayFormData
): Promise<TransientStayTableRow> {
  const { data: created, error } = await supabase
    .from('transient_stays')
    .insert({
      organization_id: organizationId,
      location_id: data.location_id,
      client_id: data.client_id,
      aircraft_id: data.aircraft_id,
      aircraft_type_id: data.aircraft_type_id ?? null,
      unit_id: data.unit_id,
      check_in: data.check_in,
      expected_check_out: data.expected_check_out,
      actual_check_out: data.actual_check_out ?? null,
      sqft_required: data.sqft_required ?? null,
      wingspan: data.wingspan ?? null,
      length: data.length ?? null,
      size_category: data.size_category ?? null,
      booking_type: data.booking_type,
      source: data.source ?? null,
      status: data.status,
      rate_type: data.rate_type ?? null,
      rate_amount: data.rate_amount ?? null,
      discount_amount: data.discount_amount ?? null,
      discount_reason: data.discount_reason ?? null,
      tax_amount: data.tax_amount ?? null,
      notes: data.notes ?? null,
      special_requests: data.special_requests ?? null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
    .select()
    .single()

  if (error) throw error

  // Fetch with full joins for return
  return fetchTransientStayById(created.id)
}

/**
 * Update an existing transient stay
 */
export async function updateTransientStay(
  id: string,
  data: Partial<TransientStayFormData>
): Promise<TransientStayTableRow> {
  const updateData: Record<string, unknown> = {}

  // Only include fields that are explicitly provided
  if (data.location_id !== undefined) updateData.location_id = data.location_id
  if (data.client_id !== undefined) updateData.client_id = data.client_id
  if (data.aircraft_id !== undefined) updateData.aircraft_id = data.aircraft_id
  if (data.aircraft_type_id !== undefined) updateData.aircraft_type_id = data.aircraft_type_id
  if (data.unit_id !== undefined) updateData.unit_id = data.unit_id
  if (data.check_in !== undefined) updateData.check_in = data.check_in
  if (data.expected_check_out !== undefined) updateData.expected_check_out = data.expected_check_out
  if (data.actual_check_out !== undefined) updateData.actual_check_out = data.actual_check_out
  if (data.sqft_required !== undefined) updateData.sqft_required = data.sqft_required
  if (data.wingspan !== undefined) updateData.wingspan = data.wingspan
  if (data.length !== undefined) updateData.length = data.length
  if (data.size_category !== undefined) updateData.size_category = data.size_category
  if (data.booking_type !== undefined) updateData.booking_type = data.booking_type
  if (data.source !== undefined) updateData.source = data.source
  if (data.status !== undefined) updateData.status = data.status
  if (data.rate_type !== undefined) updateData.rate_type = data.rate_type
  if (data.rate_amount !== undefined) updateData.rate_amount = data.rate_amount
  if (data.discount_amount !== undefined) updateData.discount_amount = data.discount_amount
  if (data.discount_reason !== undefined) updateData.discount_reason = data.discount_reason
  if (data.tax_amount !== undefined) updateData.tax_amount = data.tax_amount
  if (data.notes !== undefined) updateData.notes = data.notes
  if (data.special_requests !== undefined) updateData.special_requests = data.special_requests
  if (data.allocation_id !== undefined) updateData.allocation_id = data.allocation_id

  const { error } = await supabase
    .from('transient_stays')
    .update(updateData)
    .eq('id', id)

  if (error) throw error

  // Fetch with full joins for return
  return fetchTransientStayById(id)
}

/**
 * Soft delete a transient stay
 */
export async function deleteTransientStay(id: string): Promise<void> {
  const { error } = await supabase
    .from('transient_stays')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

/**
 * Cancel a transient stay (from inquiry/reserved/confirmed)
 */
export async function cancelTransientStay(
  id: string,
  params?: { cancellation_reason?: string }
): Promise<void> {
  const updateData: Record<string, unknown> = { status: 'cancelled' }
  if (params?.cancellation_reason) {
    updateData.cancellation_reason = params.cancellation_reason
  }

  const { error } = await supabase
    .from('transient_stays')
    .update(updateData)
    .eq('id', id)

  if (error) throw error
}

/**
 * Check out a transient stay (from checked_in)
 * Sets actual_check_out to now
 */
export async function checkOutTransientStay(id: string): Promise<void> {
  const { error } = await supabase
    .from('transient_stays')
    .update({
      status: 'checked_out',
      actual_check_out: new Date().toISOString(),
    })
    .eq('id', id)

  if (error) throw error
}

/**
 * Mark a transient stay as no-show (from reserved/confirmed)
 */
export async function noShowTransientStay(id: string): Promise<void> {
  const { error } = await supabase
    .from('transient_stays')
    .update({ status: 'no_show' })
    .eq('id', id)

  if (error) throw error
}

/**
 * Terminate a transient stay early (from checked_in)
 * Sets status to checked_out with actual_check_out and optional reason
 */
export async function terminateTransientStay(
  id: string,
  params?: { termination_reason?: string }
): Promise<void> {
  const updateData: Record<string, unknown> = {
    status: 'checked_out',
    actual_check_out: new Date().toISOString(),
  }
  if (params?.termination_reason) {
    updateData.cancellation_reason = params.termination_reason
  }

  const { error } = await supabase
    .from('transient_stays')
    .update(updateData)
    .eq('id', id)

  if (error) throw error
}
